import * as React from 'react';
import axios from 'axios';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Grid,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import useAuthorize from '../hooks/useAuthorize';
import authProvider from '../auth/authProvider';
import { SEO_KEYWORD_WRITE_ROLES, SEO_PORTAL_ROLES } from '../roles/portalRoles';
import KeywordPerformancePanel from '../products/KeywordPerformancePanel';

type OverviewRow = {
    product_id: number;
    product_name: string;
    category_name: string | null;
    tracked_keywords: number;
    keywords_with_rank: number;
    latest_average_rank: number | null;
    rank_delta_7d: number | null;
    rank_delta_14d: number | null;
    rank_delta_30d: number | null;
    rank_delta_90d: number | null;
    impressions: number;
    clicks: number;
    ctr: number;
    impressions_delta: number;
    clicks_delta: number;
    latest_rank_snapshot_date: string | null;
    latest_gsc_snapshot_date: string | null;
};

type OverviewResponse = {
    total: number;
    range: {
        from_date: string;
        to_date: string;
        range_days: number;
    };
    data: OverviewRow[];
};

const rangeOptions = [
    { label: '7 Days', value: 7 },
    { label: '14 Days', value: 14 },
    { label: '30 Days', value: 30 },
    { label: '90 Days', value: 90 },
];

const getApiBase = (): string => {
    const root = String(process.env.REACT_APP_API_URL || '').trim();
    if (!root) return '/api';
    if (root.endsWith('/api')) return root;
    if (root.endsWith('/')) return `${root}api`;
    return `${root}/api`;
};

const getAuthHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('user_authToken');
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
};

const formatNumber = (value: number | null | undefined, digits = 2): string => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
    return Number(value).toFixed(digits);
};

const formatInteger = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
    return Number(value).toLocaleString();
};

const formatSigned = (value: number | null | undefined, digits = 2): string => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
    const n = Number(value);
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(digits)}`;
};

const DashboardSeo: React.FC = () => {
    useAuthorize(SEO_PORTAL_ROLES);
    const apiBase = React.useMemo(() => getApiBase(), []);

    const [overviewRange, setOverviewRange] = React.useState<number>(7);
    const [searchInput, setSearchInput] = React.useState<string>('');
    const [searchQuery, setSearchQuery] = React.useState<string>('');
    const [productFilterId, setProductFilterId] = React.useState<number | ''>('');
    const [loading, setLoading] = React.useState<boolean>(true);
    const [error, setError] = React.useState<string>('');
    const [overview, setOverview] = React.useState<OverviewResponse | null>(null);
    const [selectedProductId, setSelectedProductId] = React.useState<number | null>(null);
    const [refreshingKeywords, setRefreshingKeywords] = React.useState(false);
    const [keywordRefreshMessage, setKeywordRefreshMessage] = React.useState<string>('');

    const canRefreshAnchorKeywords = React.useMemo(
        () => authProvider.isRoleAuthorized(SEO_KEYWORD_WRITE_ROLES),
        []
    );

    const selectedProduct = React.useMemo(
        () => overview?.data.find((row) => row.product_id === selectedProductId) || null,
        [overview, selectedProductId]
    );

    const loadOverview = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get<OverviewResponse>(
                `${apiBase}/seo-keywords/dashboard/overview`,
                {
                    params: {
                        range_days: overviewRange,
                        q: searchQuery,
                                product_id: productFilterId || undefined,
                    },
                    headers: getAuthHeaders(),
                }
            );
            setOverview(response.data);
            const firstProductId = response.data.data.length ? response.data.data[0].product_id : null;
            setSelectedProductId((current) => {
                if (!firstProductId) return null;
                if (current && response.data.data.some((row) => row.product_id === current)) return current;
                return firstProductId;
            });
        } catch (err: any) {
            const message = err?.response?.data?.error || err?.message || 'Failed to load SEO overview';
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [apiBase, overviewRange, searchQuery, productFilterId]);

    const refreshAnchorKeywords = React.useCallback(async () => {
        setRefreshingKeywords(true);
        setKeywordRefreshMessage('');
        try {
            const response = await axios.post<{
                anchor_products: number;
                products_refreshed: number;
                keywords_upserted: number;
            }>(
                `${apiBase}/seo-keywords/refresh-anchors`,
                {},
                { headers: getAuthHeaders() }
            );
            const { anchor_products, products_refreshed, keywords_upserted } = response.data;
            setKeywordRefreshMessage(
                `Updated tracked keywords for ${products_refreshed} of ${anchor_products} anchor products (${keywords_upserted} keyword rows upserted).`
            );
            await loadOverview();
        } catch (err: any) {
            const message = err?.response?.data?.error || err?.message || 'Failed to refresh anchor keywords';
            setKeywordRefreshMessage(message);
        } finally {
            setRefreshingKeywords(false);
        }
    }, [apiBase, loadOverview]);

    React.useEffect(() => {
        void loadOverview();
    }, [loadOverview]);

    const improving7d = React.useMemo(
        () => (overview?.data || []).filter((row) => (row.rank_delta_7d || 0) > 0).length,
        [overview]
    );

    return (
        <Box sx={{ p: 2 }}>
            <Card>
                <CardHeader
                    title="SEO Dashboard"
                    subheader="Track keyword rank progress for product pages. Tracked products are those with active keywords; use “Sync anchor keywords” after changing Always Visible on products."
                />
                <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                        <Select
                            size="small"
                            value={overviewRange}
                            onChange={(event) => setOverviewRange(Number(event.target.value))}
                            sx={{ minWidth: 140 }}
                        >
                            {rangeOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                        <TextField
                            size="small"
                            fullWidth
                            label="Filter products"
                            placeholder="Search product name, grade, or category"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    setSearchQuery(searchInput.trim());
                                }
                            }}
                        />
                        <Select
                            size="small"
                            value={productFilterId}
                            displayEmpty
                            onChange={(event) => {
                                const value = event.target.value;
                                setProductFilterId(value === '' ? '' : Number(value));
                            }}
                            sx={{ minWidth: 200 }}
                        >
                            <MenuItem value="">All products</MenuItem>
                            {(overview?.data || []).map((row) => (
                                <MenuItem key={row.product_id} value={row.product_id}>
                                    {row.product_name}
                                </MenuItem>
                            ))}
                        </Select>
                        <Button
                            variant="contained"
                            onClick={() => setSearchQuery(searchInput.trim())}
                        >
                            Apply
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setSearchInput('');
                                setSearchQuery('');
                                setProductFilterId('');
                            }}
                        >
                            Clear
                        </Button>
                        <Button variant="outlined" onClick={() => void loadOverview()}>
                            Reload metrics
                        </Button>
                        {canRefreshAnchorKeywords ? (
                            <Button
                                variant="contained"
                                color="secondary"
                                disabled={refreshingKeywords}
                                onClick={() => void refreshAnchorKeywords()}
                            >
                                {refreshingKeywords ? 'Syncing…' : 'Sync anchor keywords'}
                            </Button>
                        ) : null}
                    </Stack>

                    {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
                    {keywordRefreshMessage ? (
                        <Alert
                            severity={keywordRefreshMessage.includes('Failed') ? 'error' : 'success'}
                            sx={{ mb: 2 }}
                            onClose={() => setKeywordRefreshMessage('')}
                        >
                            {keywordRefreshMessage}
                        </Alert>
                    ) : null}

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="body2" color="text.secondary">Tracked Products</Typography>
                                <Typography variant="h6">{formatInteger(overview?.total || 0)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="body2" color="text.secondary">Improving in 7d</Typography>
                                <Typography variant="h6">{formatInteger(improving7d)}</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="body2" color="text.secondary">Window</Typography>
                                <Typography variant="h6">{overview?.range?.range_days || overviewRange} days</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                <Typography variant="body2" color="text.secondary">Data Through</Typography>
                                <Typography variant="h6">{overview?.range?.to_date || '--'}</Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Table size="small" sx={{ mb: 2 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Product</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell align="right">Tracked Keywords</TableCell>
                                <TableCell align="right">Avg Rank</TableCell>
                                <TableCell align="right">7d Delta</TableCell>
                                <TableCell align="right">14d Delta</TableCell>
                                <TableCell align="right">30d Delta</TableCell>
                                <TableCell align="right">90d Delta</TableCell>
                                <TableCell align="right">Impressions</TableCell>
                                <TableCell align="right">Clicks</TableCell>
                                <TableCell align="right">CTR</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading && (overview?.data || []).length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={11}>
                                        <Typography variant="body2" color="text.secondary">
                                            No tracked products match the current filter.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {(overview?.data || []).map((row) => (
                                <TableRow
                                    key={row.product_id}
                                    hover
                                    selected={row.product_id === selectedProductId}
                                >
                                    <TableCell>
                                        <Button
                                            size="small"
                                            onClick={() => setSelectedProductId(row.product_id)}
                                            sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                                        >
                                            {row.product_name}
                                        </Button>
                                    </TableCell>
                                    <TableCell>{row.category_name || '--'}</TableCell>
                                    <TableCell align="right">{formatInteger(row.tracked_keywords)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.latest_average_rank)}</TableCell>
                                    <TableCell align="right">{formatSigned(row.rank_delta_7d)}</TableCell>
                                    <TableCell align="right">{formatSigned(row.rank_delta_14d)}</TableCell>
                                    <TableCell align="right">{formatSigned(row.rank_delta_30d)}</TableCell>
                                    <TableCell align="right">{formatSigned(row.rank_delta_90d)}</TableCell>
                                    <TableCell align="right">{formatInteger(row.impressions)}</TableCell>
                                    <TableCell align="right">{formatInteger(row.clicks)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.ctr)}%</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {selectedProduct ? (
                        <KeywordPerformancePanel
                            productId={selectedProduct.product_id}
                            title={`SEO Detail · ${selectedProduct.product_name}`}
                        />
                    ) : null}
                </CardContent>
            </Card>
        </Box>
    );
};

export default DashboardSeo;
